import { useState, useCallback, useRef } from 'react';
import { likeService } from '@/features/likes/services/likeService';
import { useAuth } from '@/hooks/useAuth';
import type { Business, Event, Artist } from '@/features/clubs/types/types';

export interface UseProfileFavouritesReturn {
    likedClubs: Business[];
    likedEvents: Event[];
    likedArtists: Artist[];
    loading: boolean;
    error: string | null;
    fetchFavourites: () => Promise<void>;
    // Like toggle handlers
    unlikedClubIds: Set<string>;
    unlikedEventIds: Set<string>;
    unlikedArtistIds: Set<string>;
    handleClubLikeToggle: (clubId: string, liked: boolean) => void;
    handleEventLikeToggle: (eventId: string, liked: boolean) => void;
    handleArtistLikeToggle: (artistId: string, liked: boolean) => void;
}

export const useProfileFavourites = (): UseProfileFavouritesReturn => {
    const { isAuthenticated, isGuest } = useAuth();
    const isRealUser = isAuthenticated && !isGuest;

    const [likedClubs, setLikedClubs] = useState<Business[]>([]);
    const [likedEvents, setLikedEvents] = useState<Event[]>([]);
    const [likedArtists, setLikedArtists] = useState<Artist[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // State for tracking unliked items (they stay visible but show as unliked)
    const [unlikedClubIds, setUnlikedClubIds] = useState<Set<string>>(new Set());
    const [unlikedEventIds, setUnlikedEventIds] = useState<Set<string>>(new Set());
    const [unlikedArtistIds, setUnlikedArtistIds] = useState<Set<string>>(new Set());

    // Track if data was already fetched to prevent duplicate calls
    const fetchedRef = useRef(false);

    const fetchFavourites = useCallback(async () => {
        // Skip if already fetched, currently loading, or not a real user
        if (fetchedRef.current || loading || !isRealUser) {
            return;
        }

        try {
            setLoading(true);
            const [clubs, events, artists] = await Promise.all([
                likeService.fetchLikedBusinesses(),
                likeService.fetchLikedEvents(),
                likeService.fetchLikedArtists(),
            ]);
            setLikedClubs(clubs);
            setLikedEvents(events);
            setLikedArtists(artists);
            setError(null);
            fetchedRef.current = true;
        } catch (err) {
            console.error('Error fetching liked entities:', err);
            setError('Failed to load favourites');
        } finally {
            setLoading(false);
        }
    }, [isRealUser, loading]);

    // Handle like toggle - toggle between liked and unliked state
    const handleClubLikeToggle = useCallback((clubId: string, liked: boolean) => {
        if (liked) {
            setUnlikedClubIds(prev => {
                const next = new Set(prev);
                next.delete(clubId);
                return next;
            });
        } else {
            setUnlikedClubIds(prev => new Set(prev).add(clubId));
        }
    }, []);

    const handleEventLikeToggle = useCallback((eventId: string, liked: boolean) => {
        if (liked) {
            setUnlikedEventIds(prev => {
                const next = new Set(prev);
                next.delete(eventId);
                return next;
            });
        } else {
            setUnlikedEventIds(prev => new Set(prev).add(eventId));
        }
    }, []);

    const handleArtistLikeToggle = useCallback((artistId: string, liked: boolean) => {
        if (liked) {
            setUnlikedArtistIds(prev => {
                const next = new Set(prev);
                next.delete(artistId);
                return next;
            });
        } else {
            setUnlikedArtistIds(prev => new Set(prev).add(artistId));
        }
    }, []);

    return {
        likedClubs,
        likedEvents,
        likedArtists,
        loading,
        error,
        fetchFavourites,
        unlikedClubIds,
        unlikedEventIds,
        unlikedArtistIds,
        handleClubLikeToggle,
        handleEventLikeToggle,
        handleArtistLikeToggle,
    };
};
